import React, { useState } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';

const BulkUploadModal = ({ 
  isOpen, 
  onClose, 
  templateColumns, 
  templateData,
  onUpload, 
  entityName,
  filename,
  instructions,
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  if (!isOpen) return null;

  const handleDownloadTemplate = () => {
    // Create worksheet with template data
    const ws = XLSX.utils.json_to_sheet([templateData]);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, entityName);
    
    // Download
    XLSX.writeFile(wb, `${filename}_Template.xlsx`);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
    
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.csv')) {
      alert('Please upload a valid Excel (.xlsx) or CSV (.csv) file');
      return;
    }
    
    setFile(selectedFile);
    setResults(null);
  };

  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
          
          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Convert to JSON with raw values preserved and date formatting
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd' });
          
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => reject(error);
      reader.readAsBinaryString(file);
    });
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file to upload');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      // Parse file
      const data = await parseFile(file);
      
      if (!data || data.length === 0) {
        alert('The uploaded file is empty or invalid');
        setUploading(false);
        return;
      }

      // Call the upload handler passed from parent
      const uploadResults = await onUpload(data);
      
      setResults(uploadResults);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to process file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResults(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-indigo-600 to-indigo-700">
          <div className="flex items-center">
            <Upload size={24} className="mr-3 text-white" />
            <h3 className="text-xl font-semibold text-white">Bulk Upload {entityName}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-indigo-800 rounded p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Step 1: Download Template */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3 flex items-center">
              <FileSpreadsheet size={18} className="mr-2 text-indigo-600" />
              Step 1: Download Template
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              Download the Excel template, fill in your data, and upload it back.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download size={18} className="mr-2" />
              Download Template
            </button>
            {instructions && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 leading-relaxed" data-testid="bulk-instructions">
                {instructions}
              </div>
            )}
          </div>

          {/* Step 2: Upload File */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3 flex items-center">
              <Upload size={18} className="mr-2 text-indigo-600" />
              Step 2: Upload Filled Template
            </h4>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center ${
                dragActive ? 'border-indigo-600 bg-indigo-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {!file ? (
                <>
                  <Upload size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600 mb-2">Drag and drop your file here, or</p>
                  <label className="cursor-pointer">
                    <span className="text-indigo-600 hover:text-indigo-700 font-medium">
                      browse to upload
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx,.csv"
                      onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Supports: .xlsx, .csv</p>
                </>
              ) : (
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded">
                  <div className="flex items-center">
                    <FileSpreadsheet size={24} className="text-green-600 mr-3" />
                    <div className="text-left">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <button
                    onClick={handleReset}
                    className="text-red-600 hover:text-red-700"
                    disabled={uploading}
                  >
                    <X size={20} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="mb-4">
              <h4 className="font-semibold mb-3">Upload Results</h4>
              <div className="space-y-2">
                <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded">
                  <CheckCircle size={20} className="text-green-600 mr-2" />
                  <span className="text-green-800">
                    Successfully created: <strong>{results.success}</strong> records
                  </span>
                </div>
                {results.receipts_pending_approval > 0 && (
                  <div className="flex items-center p-3 bg-amber-50 border border-amber-200 rounded" data-testid="receipts-pending-result">
                    <AlertCircle size={20} className="text-amber-600 mr-2" />
                    <span className="text-amber-800">
                      <strong>{results.receipts_pending_approval}</strong> Drawdown Received row{results.receipts_pending_approval > 1 ? 's' : ''} saved as <strong>pending approval</strong>. Visit the Drawdown detail page → Investors tab to approve them.
                    </span>
                  </div>
                )}
                
                {results.failed > 0 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center mb-2">
                      <AlertCircle size={20} className="text-red-600 mr-2" />
                      <span className="text-red-800">
                        Failed: <strong>{results.failed}</strong> records
                      </span>
                    </div>
                    {results.errors && results.errors.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto">
                        <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                        <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                          {results.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            disabled={uploading}
          >
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  Upload
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
